import React from 'react';
import { useRestoMod } from '../../context/RestoModContext';
import { calculateProjectBudgetSummary } from '../../utils/budgetCalculations';
import { DollarSign, ShieldAlert, CheckCircle2, PackageCheck, Wrench } from 'lucide-react';
import { RESTORATION_PHASES } from '../../types/teardown';

export const StatusBar: React.FC = () => {
  const { state } = useRestoMod();

  const summary = calculateProjectBudgetSummary(
    state.project.budgetCap || 85000,
    state.bomItems
  );

  const totalFastenerCount = state.hardwareBags.reduce((acc, bag) => {
    return acc + bag.fasteners.reduce((fAcc, f) => fAcc + (f.quantityOriginal || 0), 0);
  }, 0);

  const activePhaseMeta = RESTORATION_PHASES[state.activePhase];

  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(val);
  };

  return (
    <footer className="app-status-bar bg-shop-dark border-t border-shop-border px-4 py-2 text-shop-light shrink-0">
      <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-3 text-xs">
        {/* Left Stats: Active Phase & Hardware counts */}
        <div className="flex items-center gap-4 flex-wrap">
          {/* Active Phase */}
          <div className="flex items-center gap-1.5 font-mono">
            <span className="text-shop-muted">PHASE:</span>
            <span className="font-bold text-safety-amber bg-shop-darkest px-2 py-0.5 rounded border border-shop-border flex items-center gap-1">
              <Wrench className="w-3.5 h-3.5 text-safety-amber" />
              <span>{activePhaseMeta?.title || state.activePhase}</span>
            </span>
          </div>

          {/* Sub-Assemblies & Baggies */}
          <div className="flex items-center gap-1.5 font-mono">
            <span className="text-shop-muted">BINS / BAGGIES:</span>
            <span className="font-bold text-shop-highlight bg-shop-darkest px-2 py-0.5 rounded border border-shop-border flex items-center gap-1">
              <PackageCheck className="w-3.5 h-3.5 text-blueprint-cyan" />
              <span>
                {state.subAssemblies.length} Bins | {state.hardwareBags.length} Bags ({totalFastenerCount} Fasteners)
              </span>
            </span>
          </div>
        </div>

        {/* Right Stats: Financial & Budget Rollups */}
        <div className="flex items-center gap-4 flex-wrap font-mono ml-auto">
          {/* Budget Cap */}
          <div className="flex items-center gap-1">
            <span className="text-shop-muted">BUDGET CAP:</span>
            <span className="font-bold text-shop-light">
              {formatCurrency(summary.totalAllocatedBudget)}
            </span>
          </div>

          {/* Actual Spent */}
          <div className="flex items-center gap-1">
            <span className="text-shop-muted">ACTUAL SPENT:</span>
            <span className="font-bold text-safety-amber flex items-center">
              <DollarSign className="w-3.5 h-3.5" />
              {formatCurrency(summary.totalActualSpent)}
            </span>
          </div>

          {/* Remaining */}
          <div className="flex items-center gap-1">
            <span className="text-shop-muted">REMAINING:</span>
            <span
              className={`font-bold ${
                summary.remainingBudget >= 0 ? 'text-safety-green' : 'text-safety-red'
              }`}
            >
              {formatCurrency(summary.remainingBudget)}
            </span>
          </div>

          {/* Projected Variance Badge */}
          <div className="flex items-center gap-1">
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[11px] font-bold ${
                summary.projectedBudgetVariance <= 0
                  ? 'bg-[#064e3b] text-[#ecfdf5] border-[#10b981]'
                  : 'bg-[#7f1d1d] text-[#fef2f2] border-[#ef4444]'
              }`}
              title="Projected total expenditure (Actual Spent + Pending Needed) vs Budget Cap"
            >
              {summary.projectedBudgetVariance <= 0 ? (
                <CheckCircle2 className="w-3 h-3 text-[#10b981]" />
              ) : (
                <ShieldAlert className="w-3 h-3 text-[#ef4444]" />
              )}
              <span>
                {summary.projectedBudgetVariance <= 0 ? 'UNDER CAP' : 'OVER BUDGET'}{' '}
                ({summary.projectedBudgetVariance <= 0 ? '-' : '+'}
                {formatCurrency(Math.abs(summary.projectedBudgetVariance))})
              </span>
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};
